import { GetServerSidePropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { destroyCookie, parseCookies } from 'nookies';
import { useEffect, useRef } from 'react';

import { NS } from '@/constants';
import { AddressModel } from '@/models';
import { CallToActionKind, UntypedObject, valueof } from '@/types';
import GoogleTagManager from '@/components/layout/GoogleTagManager';

import { useAppDispatch, useAppSelector } from '@/redux';
import { initializeStore } from '@/redux/initialize-store';
import { selectIsAdminActAsCustomer } from '@/redux/slices/admin.slice';
import { getCart, selectCartContent } from '@/redux/slices/cart.slice';
import {
  lastOrderHasStorageProducts,
  selectCurrentStepLabel,
  selectLastOrder, selectPaymentModes,
  selectPaymentStatus,
  setServerSideStep
} from '@/redux/slices/checkout.slice';
import { selectStoreId } from '@/redux/slices/root.slice';
import { addToast } from '@/redux/slices/toast.slice';
import { selectUserAddresses, selectUserNewsLetterSubscriptionStatus } from '@/redux/slices/user.slice';
import { getBlockingMotives } from '@/redux/thunks/checkout/getBlockingMotives';
import { getLastUserOrder } from '@/redux/thunks/checkout/getLastUserOrder';
import { getPaymentModesFromCheckout } from '@/redux/thunks/checkout/getPaiementModes';
import { getShippingModesForCartFromCheckout } from '@/redux/thunks/checkout/getShippingModes';
import { toggleAddressFormModal } from '@/redux/slices/expro.slice';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { validatePayment } from '@/redux/thunks/checkout/validatePayment';

import { useServices } from '@/services/useServices';
import { getI18NextConfigForLocale } from '@/utils/locales';
import { sanitizeObject } from '@/utils/millesimaUtils';
import { prefetchLayout } from '@/utils/prefetchLayout';
import { useSidebarPresenter } from '@/components/checkout/section/sidebar/presenters';

import AdminFields from '@/components/admin/checkout/AdminFields';
import MessageEditor from '@/components/checkout/common/MessageEditor';
import { AddressSection } from '@/components/checkout/section/AddressSection';
import { CartRecapSection } from '@/components/checkout/section/CartRecap.section';
import { CheckoutSidebarSection } from '@/components/checkout/section/CheckoutSidebar.section';
import { LoginSection } from '@/components/checkout/section/LoginSection';
import { PaymentSection } from '@/components/checkout/section/PaymentSection';
import { ReportSection } from '@/components/checkout/section/ReportSection';
import { ReviewSection } from '@/components/checkout/section/ReviewSection';
import { StepProgressSection } from '@/components/checkout/section/StepProgressSection';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import CardDS from '@/components/common/design-system/Card.ds';
import NewsletterCardContent from '@/components/checkout/newsletter/NewsletterCardContent';
import ESpotsLoader from '@/components/layout/espots/ESpotsLoader';
import LegalAgeModal from '@/components/layout/modals/LegalAgeModal';

import styles from './index.module.scss';
import CheckCgv from '@/components/checkout/common/Summary/CheckCgv';
import { validateCurrentCheckoutStepOrRedirect } from '@/redux/thunks/checkout/validateCurrentCheckoutStepOrRedirect';
import { getLastUserStorageOrder } from '@/redux/thunks/checkout/getLastUserStorageOrder';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import Head from 'next/head';
import { getShippingList } from '@/redux/thunks/getShippingList';
import { ShippingList } from '@/components/checkout/shipMode/ShippingList';
import { showSelectGiftModal } from '@/redux/slices/gui.slice';
import { ESPOTS } from '@/constants/espot';
import { FOOTER_TYPE } from '@/constants/footer';
import { HEADER_TYPE } from '@/constants/header';
import { PAYMENT_PLATFORMS, STEPS } from '@/constants/checkout';
import { STORE_ID } from '@/constants/stores';
import { checkIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/checkIncompatibleProducts';

const NAMESPACES = [
  NS.COMMON,
  NS.CHECKOUT,
  NS.CUSTOMIZATION,
  NS.ADMIN,
  NS.CELLAR,
  NS.ALTER
];

const messageEditorFromToLabels = [
  'checkout:cartReview.giftMessageTitle',
  'checkout:cartReview.addGiftMessage',
  'forms.formMessage.remainingCharacter',
  'forms.formMessage.placeholder'
];

const GIFT_MSG_MAX_LENGTH = 260;
const GIFT_MSG_MAX_LENGTH_USA = 500;

export default function CheckoutStep2({ providerToValidate, argsToValidatePayment, isPaymentToValidate }) {
  const { t } = useTranslation(NAMESPACES);

  const dispatch = useAppDispatch();
  const storeId = useAppSelector(selectStoreId);
  const cartContent = useAppSelector(selectCartContent);
  const lastOrder = useAppSelector(selectLastOrder);
  const currentStep = useAppSelector(selectCurrentStepLabel);
  const isActingAsCustomer = useAppSelector(selectIsAdminActAsCustomer);
  const paymentStatus = useAppSelector(selectPaymentStatus);
  const isUserSubscribedToNewsletter = useAppSelector(selectUserNewsLetterSubscriptionStatus);
  const isDesktop = useScreenSizeContext().isDesktop;
  const datalayerService = useServices().datalayerService;
  const cartStorage = useAppSelector(({ storage }) => storage.cartStorage);
  const lastStorageOrder = useAppSelector(({ storage }) => storage.lastStorageOrder);
  const shortUrl = useAppSelector(({ root }) => root.storeConf.shortUrl);
  const currencyISO = useAppSelector(({ root }) => root.storeConf.currencyISO);
  const user = useAppSelector(({ user }) => user.content);
  const noValidationHasAlreadyStarted = useRef(true);

  const urlPath = useRef<string>(currentStep);

  if (typeof window !== 'undefined' && window.location.pathname.split('/').at(2) !== urlPath.current) {
    urlPath.current = window.location.pathname.split('/').at(2);
  }

  if (
    currentStep === 'pay' && isPaymentToValidate && providerToValidate &&
    noValidationHasAlreadyStarted.current && typeof window !== 'undefined'
  ) {
    noValidationHasAlreadyStarted.current = false;
    dispatch(validatePayment({ provider: providerToValidate, transactionId: argsToValidatePayment?.transactionId }));
  }

  const sidebarPresenter = useSidebarPresenter();

  const lastOrderOrCurrentCart = currentStep === 'report' ? lastOrder : cartContent;
  const lastStorageOrderOrCurrentStorageCart = currentStep === 'report' ? [lastStorageOrder] : cartStorage;

  const noRegularNorStorageProducts = lastOrderOrCurrentCart.products.length === 0 &&
    lastStorageOrderOrCurrentStorageCart.length === 0;

  const containerClassName = [
    styles['checkout-container'],
    noRegularNorStorageProducts && styles['empty-cart']
  ].filter(Boolean).join(' ');

  const checkoutCardContainerClassNames = [
    styles['checkout-card-container'],
    ('login' === currentStep || noRegularNorStorageProducts) && styles['full-layout'],
    ('pay' === currentStep && paymentStatus === 'pending') && styles['full-layout'],
  ].filter(Boolean).join(' ');

  const checkoutCardClassName = [
    styles['checkout-card'],
    currentStep === 'review' && styles['without-gap'],
    ['shipping', 'pay'].includes(currentStep) && styles['little-gap'],
    noRegularNorStorageProducts && styles['with-padding']
  ].filter(Boolean).join(' ');

  const updateCartGiftMessage = ({ attributeName }) => (value: UntypedObject) => {
    dispatch(updateCartAttributeByName({ attributeName, newValue: value || '' }));
  };

  useEffect(() => {
    const pageNameMap = {
      cart: 'Cart',
      report: 'Confirmation',
      default: 'tunnel'
    };

    const pageName = pageNameMap[urlPath.current] || pageNameMap.default;

    if (typeof window !== 'undefined') {
      datalayerService.pushPageName(pageName, urlPath.current);
    }
  }, [urlPath.current]);

  useEffect(() => {
    if (currentStep === 'pay' && isPaymentToValidate && providerToValidate) {
      return;
    }
    dispatch(validateCurrentCheckoutStepOrRedirect({ type: 'redirectOnly' }));
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'report') {
      if (lastOrder?.products && user && typeof window !== 'undefined') {
        datalayerService.pushOrderAndPageName(lastOrder, currencyISO, 'Confirmation', user);
      }
    } else {
      if (cartContent?.id && currencyISO && typeof window !== 'undefined') {
        datalayerService.pushOrder(cartContent, currencyISO);
      }
    }
  }, [currentStep]);

  const title = (() => {
    const keyForCurrentStep = {
      cart: 'cart',
      login: 'login',
      address: 'shippingAddress',
      billing: 'billingAddress',
      shipping: 'shipMode',
      pay: 'payMode',
      review: 'cartReview',
      report: 'report'
    };
    const key = keyForCurrentStep[currentStep];
    return t([`checkout:${key}.title`, `forms.${key}.title`]);
  })();

  const titleForCartRecap = t(['checkout:cart.title', 'forms.cart.title']);

  const sectionComponent = (() => {
    const components = {
      cart: <CartRecapSection />,
      login: <LoginSection />,
      address: <AddressSection />,
      billing: <AddressSection />,
      shipping: <ShippingList />,
      review: <ReviewSection />,
      pay: <PaymentSection />,
      report: <ReportSection />
    };

    if (currentStep === 'cart') {
      return components.cart;
    }

    if (currentStep === 'shipping') {
      return <ShippingList />;
    }

    return (
      <CardDS content={components[currentStep]} className={checkoutCardClassName}/>
    );
  })();

  const includedStepsToFollowProgressOn = ['address', 'billing', 'shipping', 'review', 'pay'];

  const showGoBackLink = ['cart', 'login'];

  return (
    <div className={containerClassName}>
      <Head>
        <GoogleTagManager />
        <title>{`${t(STEPS.CART)} - ${shortUrl}`}</title>
        <meta name="description" content={`${t(STEPS.CART)} - ${shortUrl}`} />
      </Head>
      {isDesktop && includedStepsToFollowProgressOn.includes(currentStep) && (
        <div className={styles['step-progress']}>
          <StepProgressSection />
        </div>
      )}
      <div className={styles['sub-container']}>
        {showGoBackLink.includes(currentStep) && (
          <CallToActionContainer
            className={styles['go-back']}
            withLink kind={CallToActionKind.GHOST}
            cta={{
              url: '/',
              text: { label: t('checkout:cart.resumeShopping') }
            }}
          />
        )}
        <h1 className={styles.title}>{title}</h1>
        <div className={checkoutCardContainerClassNames}>
          {currentStep === 'report' && (
            <CardDS content={
              <span className={styles['order-id-container']}>
                <p>{t('checkout:report.subTitle')} :</p>
                <p className={styles['order-id']}>{lastOrder.id || lastStorageOrder.id}</p>
              </span>
            }
            className={checkoutCardClassName}
            />
          )}
          {!['pay', 'report'].includes(currentStep) && <ESpotsLoader storeId={storeId} locations={[ESPOTS.HEADER_CART_CHECKOUT]} />}
          {!noRegularNorStorageProducts || currentStep === 'login' ? (
            <>
              {sectionComponent}
            </>
          ) : currentStep === 'cart' && (
            <CardDS
              content={ <ESpotsLoader storeId={storeId} locations={[ESPOTS.EMPTY_CART]}/>}
              className={checkoutCardClassName}
            />
          )}
          {currentStep === 'report' && !isUserSubscribedToNewsletter && (
            <CardDS content={<NewsletterCardContent />} />
          )}
          {['review', 'report'].includes(currentStep) && (
            <>
              {currentStep === 'report' ? (
                lastOrder.giftMsg && (
                  <CardDS
                    className={checkoutCardClassName}
                    content={
                      <MessageEditor
                        t={t}
                        id="gift"
                        readOnly={true}
                        message={lastOrderOrCurrentCart.giftMsg}
                        labels={messageEditorFromToLabels}
                        messageMaxLength={storeId === STORE_ID.USA ? GIFT_MSG_MAX_LENGTH_USA : GIFT_MSG_MAX_LENGTH}
                        onChange={updateCartGiftMessage({ attributeName: 'giftMsg' })}
                      />
                    }
                  />
                )) : (
                <CardDS
                  className={checkoutCardClassName}
                  content={
                    <MessageEditor
                      t={t}
                      id="gift"
                      readOnly={false}
                      message={lastOrderOrCurrentCart.giftMsg}
                      labels={messageEditorFromToLabels}
                      messageMaxLength={storeId === STORE_ID.USA ? GIFT_MSG_MAX_LENGTH_USA : GIFT_MSG_MAX_LENGTH}
                      onChange={updateCartGiftMessage({ attributeName: 'giftMsg' })}
                    />
                  }
                />
              )}

              {isActingAsCustomer && (
                <CardDS
                  className={checkoutCardClassName}
                  content={
                    <AdminFields
                      t={t}
                      cart={lastOrderOrCurrentCart}
                    />
                  }
                />
              )}
              <h2 className={styles.title}>{titleForCartRecap}</h2>
              <CartRecapSection readOnly={currentStep && currentStep === 'report'}/>
            </>
          )}
        </div>

        {'login' !== currentStep && paymentStatus !== 'pending' && !noRegularNorStorageProducts && (
          <div className={styles['sidebar-container']}>
            <CheckoutSidebarSection sidebarPresenter={sidebarPresenter} />
          </div>
        )}

        {!isDesktop && sidebarPresenter.continueButton?.props && !noRegularNorStorageProducts && (
          <div className={styles['sticky-button']}>
            {sidebarPresenter.confirmLegalConditions.shouldShow && (
              <CheckCgv
                required
                t={sidebarPresenter.translate}
                acceptCgv={sidebarPresenter.confirmLegalConditions.state}
                onChange={sidebarPresenter.confirmLegalConditions.change}
              />
            )}
            <CallToActionContainer {...sidebarPresenter.continueButton.props} >
              {sidebarPresenter.continueButton.label}
            </CallToActionContainer>
          </div>
        )}

        {storeId !== STORE_ID.USA && (
          <LegalAgeModal />
        )}
      </div>
    </div>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const store = initializeStore({ ssrContext: ctx });
  const layoutOptions = await prefetchLayout(ctx, store);
  const auth = store.getState().auth;
  const root = store.getState().root;
  const { locale } = root.storeConf || ctx;
  const translate = await serverSideTranslations(locale, NAMESPACES, getI18NextConfigForLocale(locale));
  const userIsConnected = store.getState().user.content !== undefined;
  const userAddresses = selectUserAddresses(store.getState());
  const userCart = selectCartContent(store.getState());

  const errorKey = parseCookies(ctx)['payment-error'];
  const payjunctionTransactionId = parseCookies(ctx)['payjunction-transaction-id'];
  const hipaySuccess = parseCookies(ctx)['hipay-success'];
  const adyenRedirectResult = parseCookies(ctx)['redirect-result'];
  const adyenTransactionId = parseCookies(ctx)['adyen-transaction-id'];

  let url = ctx.resolvedUrl.split('/').at(2);
  if (url.includes('?')) {
    url = url.split('?')[0];
  }
  if (!url.includes('cart') && !userIsConnected) {
    store.dispatch(setServerSideStep('login'));
  } else {
    store.dispatch(setServerSideStep(url as valueof<typeof STEPS>));
  }

  let providerToValidate = '';
  let argsToValidatePayment = {};
  let isPaymentToValidate = false;

  const paymentErrorCodes = [
    '3000001', '3000002', '3010002', '3010004', '3010006', '3010007', '3020001',
    '4000001', '4000002', '4000003', '4000007', '4010202', '4010203', '4010204', '4010205', '4010206', '4010207',
  ];

  if (errorKey) {
    const finalErrorKey = paymentErrorCodes.includes(errorKey) ? `errors.hipay.${errorKey}` : 'errors.genericErrorMessage.main';
    store.dispatch(addToast({ msg: finalErrorKey, err: true }));
    destroyCookie(ctx, 'payment-error', { path: '/' });
  }

  if (adyenRedirectResult && adyenTransactionId) {
    destroyCookie(ctx, 'redirect-result', { path: '/' });
    destroyCookie(ctx, 'adyen-transaction-id', { path: '/' });
    providerToValidate = PAYMENT_PLATFORMS.ADYEN;
    argsToValidatePayment = {
      redirectResult: adyenRedirectResult, transactionId: adyenTransactionId
    };
    isPaymentToValidate = true;
  }

  if (hipaySuccess) {
    destroyCookie(ctx, 'hipay-success', { path: '/' });
    providerToValidate = PAYMENT_PLATFORMS.HIPAY;
    isPaymentToValidate = true;
  }

  if (payjunctionTransactionId && userIsConnected) {
    destroyCookie(ctx, 'payjunction-transaction-id', { path: '/' });
    providerToValidate = PAYMENT_PLATFORMS.PAY_JUNCTION;
    argsToValidatePayment = {
      transactionId: payjunctionTransactionId,
      redirectResult: '',
    };
    isPaymentToValidate = true;
  }

  if (url === 'address') {
    if (userAddresses && userAddresses.length > 0) {
      const cartShippingAddress = selectCartContent(store.getState()).shipping;
      const cartBillingAddress = selectCartContent(store.getState()).billing;
      const defaultAddress = userAddresses?.find((a) => a.isDefault) || userAddresses[0] || {} as AddressModel;

      const finalShippingAddress = cartShippingAddress || defaultAddress;
      const finalBillingAddress = cartBillingAddress || defaultAddress;
      await store.dispatch(updateCartAttributeByName({ attributeName: 'shipping', newValue: finalShippingAddress }));
      await store.dispatch(updateCartAttributeByName({ attributeName: 'billing', newValue: finalBillingAddress }));
    } else {
      store.dispatch(toggleAddressFormModal({ opened: true }));
    }
  }

  if (url === 'billing') {
    const cartShippingAddress = selectCartContent(store.getState()).shipping;
    await store.dispatch(updateCartAttributeByName({ attributeName: 'billing', newValue: cartShippingAddress }));
  }

  if (['review', 'pay'].includes(url)) {
    await store.dispatch(checkIncompatibleProductsFromCheckout());
  }

  if (url === 'shipping') {
    const storeId = selectStoreId(store.getState());
    await store.dispatch(getShippingModesForCartFromCheckout());
    store.dispatch(getCart());
    await store.dispatch(getShippingList({ storeId }));
  }

  if (url === 'pay') {
    await store.dispatch(getPaymentModesFromCheckout());
    const defaultPaymentMode = selectPaymentModes(store.getState())[0];
    const cartPaymentMode = selectCartContent(store.getState()).paiementMode;

    const effectivePaymentMode = cartPaymentMode?._id ? cartPaymentMode : defaultPaymentMode;
    await store.dispatch(updateCartAttributeByName({ attributeName: 'paiementMode', newValue: effectivePaymentMode }));
  }

  if (url === 'review') {
    await store.dispatch(getBlockingMotives('MOTIF_BLOCAGE'));
  }

  if (url === 'report') {
    await store.dispatch(getLastUserOrder());
  }

  if (lastOrderHasStorageProducts(store.getState())) {
    await store.dispatch(getLastUserStorageOrder());
  }

  if (userCart?.gifts?.length > 0 || userCart?.giftPacks?.length > 0) {
    const hasGift = userCart?.selectedGift || userCart?.selectedGiftPack;
    if (!hasGift) {
      store.dispatch(showSelectGiftModal());
    }
  }

  return {
    props: sanitizeObject({
      initialReduxState: store.getState(),
      providerToValidate,
      argsToValidatePayment,
      isPaymentToValidate,
      ...translate,
      layoutOptions: {
        ...layoutOptions,
        withPromotionHeader: false,
        headerType: auth.loggedUser?.isAdmin ? HEADER_TYPE.CHECKOUTCSR : HEADER_TYPE.CHECKOUT,
        withNavBar: false,
        footerType: FOOTER_TYPE.CHECKOUT,
        withWineBoxModal: false
      }
    })
  };
}
